import React, { useContext } from 'react';
import Down from 'assets/svg/Down';
import DownSelected from 'assets/svg/DownSelected';
import { useLocation, useNavigate } from 'react-router-dom';
import { Popover } from 'antd';
import { trackEventMixpanel } from 'utils/mixpanel';
import { GLOBAL_NAV_HOVER, GLOBAL_NAV_NAVIGATION } from 'utils/mixpanelEventNames';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';

// Nav icon mapping - maps nav item label to SVG image filename
// SVG files are stored in /src/assets/images/navIcons/
const navIconMap: { [key: string]: string } = {
    'Commercial Planning': 'commercial_planning.svg',
    'Strategic Pricing': 'strategic_pricing.svg',
    'Price Pack Architecture': 'price_pack_architecture.svg',
    'Trade Promotion Excellence': 'trade_promotion.svg',
    'Trade Architecture': 'trade_architecture.svg',
};

interface Props {
    navOptions: any;
    mouseOverListVisible: Function;
    pathInfo: any;
}
function NavBarOptions({ navOptions, mouseOverListVisible, pathInfo }: Props) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const hideOptionGroupLabels = commonConfig?.hideOptionGroupLabels;

    // Check if options is using the new array format
    const isNewOptionsFormat = (item: any) => {
        return item.options && Array.isArray(item.options);
    };

    // Render NEW format
    const renderNewOptionsContent = (item: any) => {
        const options = item.options || [];

        return (
            <div className="bg-white min-h-[8rem] gap-9 flex justify-start rounded-b-lg font-medium font-xs">
                {options.map((option: any, idx: number) => {
                    const basePath = option.basePath || item.path?.[0];
                    const items = option.items || [];

                    return (
                        <div key={idx} className="min-w-[10rem]">
                            <ul>
                                {option.label && !hideOptionGroupLabels && (
                                    <li className="text-gray-400 mb-3 text-xs font-normal">{option.label}</li>
                                )}

                                {items.map((list: any) => (
                                    <li
                                        key={list.label}
                                        className={`mb-2 ${
                                            list?.disabled
                                                ? 'text-gray-400 cursor-not-allowed'
                                                : list?.grayedOut
                                                ? 'text-gray-400 cursor-pointer'
                                                : 'cursor-pointer hover:text-purpleFont'
                                        } ${
                                            pathInfo?.[0] === list?.path && !list?.disabled && !list?.grayedOut
                                                ? 'font-bold text-purpleFont'
                                                : 'font-normal'
                                        }`}
                                        onClick={() => {
                                            if (list?.disabled) {
                                                return;
                                            }
                                            navigate(`/${basePath}/${list.path}`);
                                            trackEventMixpanel(GLOBAL_NAV_NAVIGATION, {
                                                screen: list?.label,
                                                screenHeader: item?.label,
                                                category: option.label || null,
                                                url: `/${basePath}/${list.path}`,
                                            });
                                        }}
                                    >
                                        {list.label}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderLegacyOptionsContent = (item: any) => {
        return (
            <div className="bg-white min-h-[8rem] min-w-[10rem] gap-9 flex justify-start rounded-b-lg font-medium font-xs">
                {item.options?.Promo?.length > 0 && (
                    <div>
                        <ul>
                            {item?.showSubHeader && <li className="text-purpleFont mb-4">Promo</li>}
                            {item.options.Promo?.map((list: any) => {
                                return (
                                    <li
                                        className={`mb-2 cursor-pointer ${
                                            pathInfo?.[0] === list?.path ? 'font-bold' : 'font-normal'
                                        }`}
                                        onClick={() => {
                                            navigate(`/${item.path?.[0]}/${list.path}`);
                                            trackEventMixpanel(GLOBAL_NAV_NAVIGATION, {
                                                screen: list?.label,
                                                screenHeader: item?.label,
                                                url: `/${item.path?.[0]}/${list.path}`,
                                            });
                                        }}
                                        key={list.label}
                                    >
                                        {list.label}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                {item.options?.Pricing?.length > 0 && (
                    <div>
                        <ul>
                            {item.options.Pricing && item?.showSubHeader && (
                                <li className="text-purpleFont mb-4">Pricing</li>
                            )}
                            {item.options.Pricing?.map((list: any) => {
                                return (
                                    <li
                                        className={`mb-2 cursor-pointer ${
                                            pathInfo?.[0] === list?.path ? 'font-bold' : 'font-normal'
                                        }`}
                                        onClick={() => {
                                            navigate(`/${item.path?.[1]}/${list.path}`);
                                            trackEventMixpanel(GLOBAL_NAV_NAVIGATION, {
                                                screen: list?.label,
                                                url: `/${item.path?.[1]}/${list.path}`,
                                            });
                                        }}
                                        key={list.label}
                                    >
                                        {list.label}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    // Detects format and renders accordingly
    const renderContent = (item: any) => {
        if (isNewOptionsFormat(item)) {
            return renderNewOptionsContent(item);
        }
        return renderLegacyOptionsContent(item);
    };

    const getNavIcon = (label: string) => {
        const iconFile = navIconMap[label];
        if (!iconFile) return null;
        try {
            return require(`assets/images/navIcons/${iconFile}`);
        } catch {
            return null;
        }
    };

    return (
        <span className="flex items-center ml-6 rounded gap-x-1">
            {navOptions?.map((item: any, key: number) => {
                // Only treat a nav item as selected if the current route is exactly `/${basePath}`
                // or starts with `/${basePath}/` to avoid substring matches like "assortment" matching "assortment-planning.
                const navBasePaths: string[] = (item.path || []).filter(Boolean);
                const isSelected = navBasePaths.some((basePath) => {
                    const normalized = `/${String(basePath).replace(/^\/+/, '').replace(/\/+$/, '')}`;
                    return pathname === normalized || pathname.startsWith(`${normalized}/`);
                });
                const iconSrc = item.label ? getNavIcon(item.label) : null;

                return (
                    <Popover
                        onOpenChange={(visible) => {
                            if (visible) trackEventMixpanel(GLOBAL_NAV_HOVER, { from: item?.label });
                        }}
                        overlayInnerStyle={{ position: 'relative', top: -5 }}
                        content={() => renderContent(item)}
                        placement="bottomLeft"
                        showArrow={false}
                        trigger={'hover'}
                        key={key}
                    >
                        <div className="relative" key={item.path}>
                            <div
                                className={`flex justify-center items-center rounded gap-x-1 p-2 cursor-pointer ${
                                    isSelected ? 'font-bold bg-bgBackground' : ''
                                } `}
                            >
                                {/* {iconSrc && <img src={iconSrc} alt={item.label} className="w-4 h-4 mr-1" />} */}
                                <span className="text-[16px]">{item.label}</span>
                                <span>{isSelected ? <DownSelected /> : <Down />}</span>
                            </div>
                        </div>
                    </Popover>
                );
            })}
        </span>
    );
}

export default NavBarOptions;
